/**
 * security-brief.ts — Editorial copy layer for the Security Center standfirst.
 *
 * Mirrors `posture-brief.ts` in shape and voice rules. The Security pillar
 * tells its own story: the named risk is the top open SHALL violation (or its
 * framework citation) rather than a pillar, and the headline leads with the
 * open SHALL-violation count whenever there is one.
 *
 * Contract: every editorial slot is ONE deterministic, data-driven line.
 * There is no variant table, no seed, no rotation — the same input always
 * produces the same brief, and all variation in what the user reads comes
 * from the data (score, delta, top action, critical count), never from
 * picking among synonymous phrasings of the same fact.
 *
 * Copy says "critical", never "SHALL" (D33): the RFC 2119 keyword is a data
 * value (`criticality`), not UI copy, and brief-copy-lint L5 bans it in the
 * rendered headline, lede and verdict.
 *
 * Action-first fields (blueprint §6.2 row 4): `headline` names the ranker's
 * top action (the item the primary action and the Overview's "Three things to
 * fix first" open), else closes the critical controls, else names a
 * framework. Like posture-brief, it narrates the ranker's order and never
 * reorders it: a High item the ranker puts first stays first. `lede` names
 * the failing-control count from `failingCount` (with the critical share), and
 * falls back to the critical count alone, then the score.
 *
 * Loading-state handling: `input.score == null` short-circuits to the
 * neutral "Reading posture…" brief rather than coercing null to 0 and
 * showing a worst-band verdict for a fraction of a second before the real
 * score arrives.
 */

import {
  briefProvenance,
  bucketDelta,
  cap,
  controlPrimaryLabel,
  controlsLedeBody,
  isNegative,
  isNonNegative,
  knownCount,
  LEDE_FRAGMENTS,
  type DeltaBucket,
} from './brief-helpers'
import type { ActionBriefFields } from './brief-shape'

/* -------------------------------------------------------------------------- */
/* Public shape                                                               */
/* -------------------------------------------------------------------------- */

export interface SecurityBrief {
  /** Six words or fewer. Not drawn: the Carbon pillar brief shows no verdict (D22). */
  verdict: string
  /** The provenance line (rule 4, D36): "Assessed by Guardian at 06:12 today", or "" while the time is unknown. Kept under its old name for PillarBrief. */
  byline: string
  /** Score delta vs N days ago, for PillarBrief's delta tag on the score tile (a page can hide it). */
  delta: { value: number; days: number }
  /** Action-first h1 (blueprint §6.2 row 4). */
  headline: string
  /** The framework the headline names, set at 400 (never a pillar ink). */
  emphasis?: ActionBriefFields['emphasis']
  /** One sentence, one `**bold**` run naming the subject and its count. */
  lede: string
  /** The view's one Soft Green button; only with a `primaryHref` and open work to review. */
  primaryAction?: ActionBriefFields['primaryAction']
}

/** Lightweight named-risk shape — accepts a SHALL violation or any policy
 *  carrying a framework citation. Both fields optional so the consumer can
 *  pass whatever's available without a normalization layer. */
export interface SecurityTopAction {
  /** Imperative-friendly title, e.g. "Enforce phish-resistant MFA". */
  title: string
  /** Framework citation code, e.g. "SCuBA AAD.4.1v1" or "NIST 800-53". */
  framework?: string
  /** Open-violation criticality. Reserved for future copy variants. */
  criticality?: 'SHALL' | 'SHOULD' | 'MAY'
}

export interface SecurityBriefInput {
  /** Signed-in user email. Unused since the byline became the provenance line
   *  (rule 5 bans bylines); kept for call-site compatibility. */
  userEmail?: string
  /** When Guardian last evaluated Best Practice: null when never, undefined
   *  while unknown. Feeds the provenance line. */
  assessedAt?: string | null
  /** The provenance clock's "today"; tests only. */
  now?: Date
  /** Active tenant ID. Callers still pass this; unused by the composer now
   *  that copy is deterministic rather than seeded per tenant per day. */
  tenantId?: string
  /** Composite Guardian Score (0-100) — the page surfaces the same composite
   *  the Command Center does, so this is the same value as PostureBrief.score. */
  score?: number | null
  /** 30-day score delta: drives the verdict, the headline's cadence line and the `delta` field. */
  deltas?: { score?: number; days?: number }
  /** Top open SHALL violation (or other named risk) — drives the headline,
   *  lede, verdict, and primary action when present. */
  topAction?: SecurityTopAction
  /** Open SHALL-violation count — drives the headline and verdict. */
  criticalCount?: number
  /**
   * Open violations in the same starred-framework scope as `criticalCount`
   * (data-map X5/C13: `useViolationSummary` -> `open`, surfaced as
   * `useComplianceDashboard().dashboardData.totalViolations`). Drives the
   * lede's "{f} controls are failing". Pass `undefined` while loading, never
   * 0: the lede then falls back to the critical count alone.
   */
  failingCount?: number
  /** Where the primary button goes (the composer owns no routes). Without it,
   *  or on an all-clear or calm headline, the brief has no `primaryAction`. */
  primaryHref?: string
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

/** Drop trailing punctuation a violation title may carry through ingest. */
function trimTrailingPunct(s: string): string {
  return s.replace(/[.,;:!?]+$/, '')
}

/** Word form for 1-9, digits from 10 up. */
function countWord(n: number): string {
  const words = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine']
  return n >= 1 && n <= 9 ? words[n] : String(n)
}

/** First whitespace-separated token of a framework citation, e.g.
 *  "SCuBA AAD.4.1v1" → "SCuBA" — keeps the six-word verdict cap intact. */
function shortFramework(framework: string): string {
  return framework.trim().split(/\s+/)[0] ?? framework
}

/** Open SHALL-violation count, normalized: a finite non-negative number, or
 *  `undefined` when the caller hasn't supplied one. */
function normalizeCrit(criticalCount: number | undefined): number | undefined {
  return typeof criticalCount === 'number' && Number.isFinite(criticalCount) && criticalCount >= 0
    ? criticalCount
    : undefined
}

/** Round to one decimal, then render as an integer string when whole
 *  ("4") and one decimal place otherwise ("0.4", "2.5"). */
function formatDelta(x: number): string {
  const r = Math.round(x * 10) / 10
  return Number.isInteger(r) ? String(r) : r.toFixed(1)
}

/* -------------------------------------------------------------------------- */
/* Copy tables — one line per slot, tokens filled from data                   */
/* -------------------------------------------------------------------------- */

const VERDICT = {
  ROUTINE_WATCH: 'Routine watch only.',
  CRIT_STILL_ONE: 'One critical control still open.',
  CRIT_STILL_FEW: '{n} critical controls still open.',
  PULLING_DOWN: '{framework} is pulling the score down.',
  SCORE_DOWN: 'Score down {n} this month.',
  TIGHTEN_NEXT: 'Tighten {framework} next.',
  LEAD_NEXT: 'Take the lead item next.',
  TIGHTEN_FIRST: 'Tighten {framework} first.',
  START_LEAD: 'Start with the lead item.',
  CLOSE_SHALLS: 'Close the critical controls first.',
  WORK_QUEUE: 'Work the queue top down.',
}

/**
 * Action-first h1 (blueprint §6.2 row 4). The ranker's top action leads, then
 * the open critical controls, then a framework, then the known-zero and
 * routine lines.
 */
const HEADLINE_ACTION = {
  LOADING: 'Reading your posture.',
  CRIT_ONE: 'Close the critical control first.',
  CRIT_FEW: 'Close the {n} critical controls first.',
  TOP_ACTION: '{title} first.',
  FRAMEWORK: 'Start with {framework} this week.',
  CLEAR: 'Nothing critical to close this week.',
  CADENCE: 'Keep the usual review cadence this week.',
  QUEUE: 'Work the queue top down this week.',
}

/** The primary button: the top control, else the failing-controls count. */
const PRIMARY_ACTION = {
  FIX: 'Fix {title}',
  FAILING_ONE: 'Review the failing control',
  FAILING_FEW: 'Review the {n} failing controls',
  FAILING_ANY: 'Review the failing controls',
}

/** The lede: the failing and critical controls (`LEDE_FRAGMENTS`), else the score. */
const LEDE = {
  LOADING: '**Guardian is reading your tenant**.',
  SCORE: '**Your Guardian Score is {score}**.',
}

/* -------------------------------------------------------------------------- */
/* Verdict (six words or fewer)                                               */
/* -------------------------------------------------------------------------- */

function pickVerdict(
  score: number,
  bucket: DeltaBucket,
  n: string,
  crit: number | undefined,
  framework: string | undefined,
  title: string | undefined,
): string {
  const negative = isNegative(bucket)
  const nonNegative = isNonNegative(bucket)
  const short = framework ? shortFramework(framework) : undefined

  if (score >= 85 && nonNegative) {
    const c = crit ?? 0
    if (c >= 1) {
      if (c === 1) return VERDICT.CRIT_STILL_ONE
      return VERDICT.CRIT_STILL_FEW.replace('{n}', countWord(c))
    }
    if (short) return VERDICT.TIGHTEN_NEXT.replace('{framework}', short)
    if (title) return VERDICT.LEAD_NEXT
    return VERDICT.ROUTINE_WATCH
  }

  if (score >= 70 && negative) {
    if (short) return VERDICT.PULLING_DOWN.replace('{framework}', short)
    return VERDICT.SCORE_DOWN.replace('{n}', n)
  }

  if (score >= 70) {
    if (short) return VERDICT.TIGHTEN_NEXT.replace('{framework}', short)
    return VERDICT.LEAD_NEXT
  }

  if (score >= 55) {
    if (short) return VERDICT.TIGHTEN_FIRST.replace('{framework}', short)
    return VERDICT.START_LEAD
  }

  if (crit !== undefined && crit >= 1) return VERDICT.CLOSE_SHALLS
  return VERDICT.WORK_QUEUE
}

/* -------------------------------------------------------------------------- */
/* Action-first headline + lede (blueprint §6.2 row 4)                        */
/* -------------------------------------------------------------------------- */

function pickActionHeadline(
  crit: number | undefined,
  title: string | undefined,
  framework: string | undefined,
  score: number,
  bucket: DeltaBucket,
  failingCount: number | undefined,
): { headline: string; emphasis?: ActionBriefFields['emphasis'] } {
  // The ranker's #1 leads even while critical controls are open: the primary
  // action and the Overview's "Three things to fix first" open the same item,
  // and the lede still carries the critical count.
  if (title) return { headline: HEADLINE_ACTION.TOP_ACTION.replace('{title}', title) }
  if (crit === 1) return { headline: HEADLINE_ACTION.CRIT_ONE }
  if (crit !== undefined && crit >= 2) return { headline: HEADLINE_ACTION.CRIT_FEW.replace('{n}', String(crit)) }
  if (framework) {
    return { headline: HEADLINE_ACTION.FRAMEWORK.replace('{framework}', framework), emphasis: { text: framework } }
  }
  // No all-clear beside a lede that names failing controls (blueprint §6.4 L6).
  if (crit === 0 && !knownCount(failingCount)) return { headline: HEADLINE_ACTION.CLEAR }
  return { headline: score >= 85 && isNonNegative(bucket) ? HEADLINE_ACTION.CADENCE : HEADLINE_ACTION.QUEUE }
}

/* -------------------------------------------------------------------------- */
/* Public API                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Compose a SecurityBrief from the active Security Center state. All inputs
 * are optional — the function degrades gracefully toward a plausible "no data"
 * brief rather than throwing.
 *
 * When `input.score` is null/undefined (e.g. the page mounted before the
 * score query resolved) we return a neutral "reading" brief rather than
 * synthesizing a worst-band verdict. Without this guard the composer would
 * coerce `null → 0`, which lands the loading state in the lowest editorial
 * band and shows the user an authoritative-but-wrong verdict for a fraction
 * of a second before the real data arrives.
 *
 * Note: `score === 0` is a legitimate real value and still routes through
 * the normal band logic — only `null`/`undefined` triggers the loading brief.
 */
export function composeSecurityBrief(input: SecurityBriefInput = {}): SecurityBrief {
  const deltaValue = typeof input.deltas?.score === 'number' ? input.deltas.score : 0
  const deltaDays = typeof input.deltas?.days === 'number' ? input.deltas.days : 30

  const byline = briefProvenance(input.assessedAt, { now: input.now })

  if (input.score == null) {
    return {
      verdict: 'Reading posture…',
      byline,
      delta: { value: deltaValue, days: deltaDays },
      headline: HEADLINE_ACTION.LOADING,
      lede: LEDE.LOADING,
    }
  }

  const score = input.score
  const bucket = bucketDelta(deltaValue)
  const n = formatDelta(Math.abs(deltaValue))

  const crit = normalizeCrit(input.criticalCount)
  const rawTitle = input.topAction?.title
  const title = rawTitle ? cap(trimTrailingPunct(rawTitle)) : undefined
  const framework = input.topAction?.framework

  const verdict = pickVerdict(score, bucket, n, crit, framework, title)

  const action = pickActionHeadline(crit, title, framework, score, bucket, input.failingCount)
  const controls = controlsLedeBody(input.failingCount, crit)
  const lede = controls ? `${controls}.` : LEDE.SCORE.replace('{score}', String(Math.round(score)))
  const href = input.primaryHref?.trim()
  const calm = action.headline === HEADLINE_ACTION.CLEAR || action.headline === HEADLINE_ACTION.CADENCE

  return {
    verdict,
    byline,
    delta: { value: deltaValue, days: deltaDays },
    headline: action.headline,
    ...(action.emphasis ? { emphasis: action.emphasis } : {}),
    lede,
    ...(href && !calm
      ? { primaryAction: { label: controlPrimaryLabel(PRIMARY_ACTION, title, input.failingCount), href } }
      : {}),
  }
}

/* -------------------------------------------------------------------------- */
/* Test-only exports                                                          */
/* -------------------------------------------------------------------------- */

/** Copy tables exposed for exact-string assertions in tests (and for a
 *  generic copy-lint sweep). Not part of the public runtime API; do not
 *  import from app code. */
export const __TEST_ONLY__ = {
  VERDICT,
  HEADLINE_ACTION,
  LEDE,
  LEDE_FRAGMENTS,
  PRIMARY_ACTION,
}
